import React from 'react'
import { Grid, FormControlLabel, Switch, Typography } from '@mui/material'
import { State } from '@hookstate/core'
import { useLang } from '~/hooks/useLang'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import 'dayjs/locale/pt-br'

dayjs.extend(utc)

interface FilterTimeIntervalProps {
  readonly enabled: State<boolean>
  readonly startTime: State<string>
  readonly endTime: State<string>
  readonly disabled?: boolean
}

export default function FilterTimeInterval({
  enabled,
  startTime,
  endTime,
  disabled = false,
}: FilterTimeIntervalProps) {
  const { translate } = useLang()

  const getTimeValue = (timeString: string): Dayjs | null => {
    if (!timeString) return null
    return dayjs(timeString).local()
  }

  const setTimeValue = (time: Dayjs | null): string => {
    if (!time || !time.isValid()) return ''
    return dayjs()
      .hour(time.hour())
      .minute(time.minute())
      .second(0)
      .millisecond(0)
      .utc()
      .toISOString()
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
      <Grid
        item
        xs={2}
        sx={{
          display: 'flex',
          flexDirection: enabled.get() ? 'column' : 'row',
          gap: 2,
          paddingTop: enabled.get() ? 1.5 : 1.2,
          paddingLeft: 1,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={enabled.get()}
              onChange={(item) => enabled.set(item.target.checked)}
              disabled={disabled}
            />
          }
          label={translate('Filter by Time Interval')}
        />

        {enabled.get() && (
          <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Grid sx={{ display: 'flex', gap: 2 }}>
              <TimePicker
                label={translate('Start Time')}
                value={getTimeValue(startTime.get())}
                onChange={(newValue) => startTime.set(setTimeValue(newValue))}
                disabled={disabled}
                ampm={false}
                slotProps={{
                  textField: {
                    size: 'small',
                    error: !!(startTime.get() && endTime.get() && startTime.get() === endTime.get()),
                  },
                }}
              />

              <TimePicker
                label={translate('End Time')}
                value={getTimeValue(endTime.get())}
                onChange={(newValue) => endTime.set(setTimeValue(newValue))}
                disabled={disabled}
                ampm={false}
                slotProps={{
                  textField: {
                    size: 'small',
                    error: !!(startTime.get() && endTime.get() && startTime.get() === endTime.get()),
                  },
                }}
              />
            </Grid>
            {startTime.get() && endTime.get() && startTime.get() === endTime.get() && (
              <Typography variant="caption" color="error">
                {translate('Start time and end time cannot be the same')}
              </Typography>
            )}
          </Grid>
        )}
      </Grid>
    </LocalizationProvider>
  )
}
